import { CustomDataTable } from '@broadlume/willow-ui'
import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'
import {
    addDays,
    format,
    isAfter,
    isBefore,
    isPast,
    isSameDay,
    isValid,
    parse,
} from 'date-fns'
import { toPng } from 'html-to-image'
import jsPDF from 'jspdf'
import _ from 'lodash'
import { useAuthHook, useGetBranches } from './hooks/auth.hooks'
import { DateRange } from './interfaces/orders.interface'
import {
    customInvoiceObject,
    GetInvoiceSearchAPIObject,
    GetOrdersOrdFileAPIObject,
    GetPendingOrderAPIObject,
    InvoiceProrateObject,
    InvoiceProrateType,
    OrderRow,
} from './interfaces/place-order.interface'
import { parseStringToDecimal } from '../pages/dashboard/dashboard.hook'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, @typescript-eslint/no-explicit-any
export const trimTrailingSpacesInArray = <T extends any>(array: T[]): T[] => {
    return array.map((obj) => {
        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === 'string') {
                obj[key] = obj[key].trimEnd()
            }
        })
        return obj
    })
}

export const isMobileOrTablet = window.matchMedia('(max-width: 768px)').matches

export const downloadPdf = async ({
    element,
    type,
}: {
    element: HTMLElement
    type?: string
}) => {
    try {
        let xOffset = 0
        let yOffset = 0

        // Apply zoom-out class for mobile and tablet devices
        let originalWidth: string | null = null
        let originalPadding: string | null = null

        if (isMobileOrTablet && type === 'order') {
            // Store the original width and padding
            originalWidth = element.style.width
            originalPadding = element.style.padding
            element.style.width = '1024px' // Set to desktop width
            element.style.padding = '0 40px' // Add padding to center the content
        }

        const dataUrl = await toPng(element, {
            cacheBust: true,
            quality: 1,
        })

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
        })

        const elementWidth = element.offsetWidth
        const elementHeight = element.offsetHeight

        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()

        const scaleFactor = Math.min(
            pageWidth / elementWidth,
            pageHeight / elementHeight
        )

        const scaledWidth = elementWidth * scaleFactor
        const scaledHeight = elementHeight * scaleFactor

        if (type === 'prorate') {
            xOffset = (pageWidth - scaledWidth) / 15
            yOffset = (pageHeight - scaledHeight) / 15
        }

        if (type === 'order') {
            xOffset = (pageWidth - scaledWidth) / 2
            yOffset = (pageHeight - scaledHeight) / 2
        }

        pdf.addImage(
            dataUrl,
            'PNG',
            xOffset,
            yOffset,
            scaledWidth,
            scaledHeight
        )
        if (isMobileOrTablet && type === 'order') {
            // Set the original width and padding back
            element.style.width = originalWidth!
            element.style.padding = originalPadding!
        }
        return pdf
    } catch (err) {
        console.error('Error generating PDF:', err)
    }
}

// Groups by the values of `keys` using literal keys. lodash path helpers are
// avoided because values containing "." or "[]" would be split into nested paths.
export const groupByDeep = (collection: Array<unknown>, keys: string[]) => {
    const result: Record<string, any> = {}
    collection.forEach((item) => {
        const path = keys.map((key) => String(_.get(item, key) ?? ''))
        let node = result
        path.slice(0, -1).forEach((segment) => {
            if (!Object.prototype.hasOwnProperty.call(node, segment)) {
                node[segment] = {}
            }
            node = node[segment]
        })
        const last = path[path.length - 1]
        if (!Object.prototype.hasOwnProperty.call(node, last)) {
            node[last] = []
        }
        node[last].push(item)
    })
    return result
}

export const generateTransactionID = (): string => {
    const year = new Date().getFullYear().toString().padStart(4, '0')
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0')
    const day = new Date().getDate().toString().padStart(2, '0')
    const hour = new Date().getHours().toString().padStart(2, '0')
    const minute = new Date().getMinutes().toString().padStart(2, '0')
    const second = new Date().getSeconds().toString().padStart(2, '0')
    const random = Math.floor(Math.random() * 100)
        .toString()
        .padStart(2, '0')

    return year.slice(-2) + month + day + hour + minute + second + random
}

export const convertDateYYYYMMDD = (date: Date) => {
    if (!date) {
        return ''
    }
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
}

export const parseAndFormatDate = (dateString: string): string | null => {
    if (dateString.length === 8) {
        let parsedDate: Date

        if (dateString.startsWith('20')) {
            // Format: "YYYYMMDD" (e.g., "20230608")
            parsedDate = parse(dateString, 'yyyyMMdd', new Date())
        } else if (parseInt(dateString.slice(0, 2), 10) <= 12) {
            // Format: "MMDDYYYY" (e.g., "02162023")
            parsedDate = parse(dateString, 'MMddyyyy', new Date())
        } else {
            // Format: "DDMMYYYY" (e.g., "06082023")
            parsedDate = parse(dateString, 'ddMMyyyy', new Date())
        }

        // Format the parsed date to MM/dd/yyyy
        return format(parsedDate, 'MM/dd/yyyy')
    } else {
        console.error(
            'Invalid date format. Expected "DDMMYYYY", "YYYYMMDD", or "MMDDYYYY".'
        )
        return null
    }
}

export function addToRange(
    /** The date to add to the range. */
    date: Date,
    /** The range where to add `date`. */
    initialRange: DateRange | undefined
): DateRange | undefined {
    const { from, to } = initialRange || {}

    let range: DateRange | undefined

    if (!from && !to) {
        // the range is empty, add the date
        range = { from: date, to: date }
    } else if (from && !to) {
        // adding date to an incomplete range
        if (isSameDay(from, date)) {
            // adding a date equal to the start of the range
            range = undefined
        } else if (isBefore(date, from)) {
            // adding a date before the start of the range
            range = { from: date, to: from }
        } else {
            // adding a date after the start of the range
            range = { from, to: date }
        }
    } else if (from && to) {
        // adding date to a complete range
        if (isBefore(date, from)) {
            // adding a date before the start of the range
            range = { from: date, to: to }
        } else if (isAfter(date, from)) {
            // adding a date after the start of the range
            range = { from, to: date }
        } else if (isAfter(date, to)) {
            // adding a date after the end of the range
            range = { from, to: date }
        } else {
            range = { from: date, to: date }
        }
    }

    return range
}

/**
 * Converts a camelCase string to a title case string.
 * @param camelCase - A string in camelCase format.
 * @returns A string converted to title case.
 */
export const camelToTitle = (camelCase: string): string =>
    camelCase
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (match) => match.toUpperCase())
        .trim()
        .replace('  ', ' ')

/**
 * Determines the checked state of a checkbox item within a list.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.item - The checkbox item to evaluate.
 * @param {string} params.item.label - The label of the checkbox item.
 * @param {string} params.item.value - The value of the checkbox item.
 * @param {string[]} params.selectedArr - Array of selected item values.
 * @param {number} params.sourceArrLength - Total number of items in the source array.
 * @returns {boolean | string} - Returns `true` if all items are selected,
 * `false` if none are selected, 'indeterminate' if some are selected,
 * or checks if the specific item is included in the selected array.
 */
export const isCheckboxChecked = ({
    selectedArr = [],
    sourceArrLength,
    item,
}: {
    item: { label: string; value: string }
    selectedArr: string[]
    sourceArrLength: number
}) => {
    if (item.label.toLowerCase() === 'select all') {
        if (selectedArr.length) {
            if (selectedArr.length === sourceArrLength) {
                return true
            } else {
                return 'indeterminate'
            }
        } else {
            return false
        }
    }
    return selectedArr.includes?.(item.value)
}

export const orderAnalysisSortFn: Parameters<
    typeof CustomDataTable
>[0]['sortFunction'] = (rows, selector, direction) => {
    return rows.sort((rowA, rowB) => {
        // use the selector function to resolve your field names by passing the sort comparitors
        let aField = selector(rowA)
        let bField = selector(rowB)
        let key = Object.entries(rowA).find(
            ([
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                key,
                value,
            ]) => value === aField
        )?.[0]
        if ([typeof aField, typeof bField].includes('object')) {
            key = aField[1] || bField[1]
            aField = aField[0]
            bField = bField[0]
        }
        if (key === 'id') {
            key = rowA?.colSelector
        }

        if (
            isEmpty.includes(aField) ||
            isEmpty.includes(bField) ||
            isEmpty.includes(rowA.id) ||
            isEmpty.includes(rowB.id)
        ) {
            return direction === 'asc' ? '-1' : '1'
        }

        return allSortFn({
            sortBy: key,
            a: rowA,
            b: rowB,
            sortByDirection: direction === 'asc' ? '-1' : '1',
        })
    })
}

const isEmpty = ['totals', undefined, '-']

const sortByName = ({
    sortByDirection,
    a,
    b,
    data,
}: {
    sortByDirection: string
    a: OrderRow | customInvoiceObject
    b: OrderRow | customInvoiceObject
    data: keyof OrderRow | keyof customInvoiceObject
}) => {
    if (a[data] === '-' || b[data] === '-') {
        return 0
    }
    if (sortByDirection === '1' && a[data] > b[data]) {
        return 1
    } else {
        return -1
    }
}

export const allSortFn = ({
    sortBy = '',
    a,
    b,
    sortByDirection,
}: {
    sortBy: string
    a: OrderRow | customInvoiceObject
    b: OrderRow | customInvoiceObject
    sortByDirection: string
}) => {
    if (
        a[sortBy] === b[sortBy] ||
        isEmpty.includes(a[sortBy]) ||
        isEmpty.includes(b[sortBy])
    ) {
        return 0
    }
    switch (sortBy) {
        case '':
        case 'totals':
            return 0

        case 'orderNumber':
            return (Number(b.id) - Number(a.id)) * Number(sortByDirection)
        case 'invoiceNumber':
        case 'amount':
        case 'balanceDue':
        case 'poNumber':
        case 'invoiceOrderNumber':
        case 'building':
        case 'saleTotal':
        case 'saleQty':
            return (
                (Number(b[sortBy]) - Number(a[sortBy])) *
                Number(sortByDirection)
            )
        case 'status':
        case 'SLS_STYLE':
        case 'orderedBy':
        case 'DisplayName':
            return sortByDirection === '1'
                ? b[sortBy].localeCompare(a[sortBy], 'en', {
                      sensitivity: 'base',
                  })
                : a[sortBy].localeCompare(b[sortBy], 'en', {
                      sensitivity: 'base',
                  })
        case 'Email':
        case 'unit':
        case 'Company':
        case 'Customer':
            return sortByDirection === '1'
                ? b[sortBy].localeCompare(a[sortBy], 'en', {
                      sensitivity: 'base',
                      numeric: true,
                  })
                : a[sortBy].localeCompare(b[sortBy], 'en', {
                      sensitivity: 'base',
                      numeric: true,
                  })

        case 'customerName':
            return sortByName({
                sortByDirection: sortByDirection,
                a: a,
                b: b,
                data: sortBy,
            })

        case 'locationCode':
            if (sortByDirection === '1' && a.orderedBy > b.orderedBy) {
                return 1
            } else {
                return -1
            }
        case 'buildingUnit':
            if (sortByDirection === '1' && a.orderedBy > b.orderedBy) {
                return 1
            } else {
                return -1
            }
        case 'last_success_login':
        case 'last_fail_login': {
            const bTime = new Date(b[sortBy]).getTime()
            const aTime = new Date(a[sortBy]).getTime()

            return sortByDirection === '1' ? bTime - aTime : aTime - bTime
        }
        case 'isSuperAdmin':
        case 'isActive':
            return sortByDirection === '1'
                ? b[sortBy] - a[sortBy]
                : a[sortBy] - b[sortBy]
        case 'invoiceDate':
        case 'dueDate':
        case 'orderDate':
        case 'installDate': {
            // Handle identical or empty date values
            if (
                a[sortBy] === b[sortBy] ||
                ['', '-'].includes(a[sortBy]) ||
                ['', '-'].includes(b[sortBy])
            ) {
                return 0
            }
            if (sortByDirection === '1' && a[sortBy] > b[sortBy]) {
                return 1
            } else {
                return -1
            }
        }

        default:
            return sortByDirection === '1'
                ? Number(a.id) - Number(b.id)
                : Number(b.id) - Number(a.id)
    }
}

/**
 * Sort an array by a single key with custom direction and handle special cases.
 * Moves empty/null/undefined values to the end or start based on direction.
 * @param array - The array to sort.
 * @param key - The key to sort by.
 * @param direction - The direction for sorting ('asc' or 'desc').
 * @returns The sorted array with empty values at the top or bottom.
 */
export const commonSort = <T>(
    array: T[],
    key: keyof T,
    direction: 'asc' | 'desc'
): T[] => {
    // Filter out elements with empty or invalid values
    const [validElements, emptyElements] = array.reduce(
        (acc, item) => {
            const value = _.get(item, key)

            if (
                value === null || // Null
                value === undefined || // Undefined
                (typeof value === 'string' && value.trim().length === 0) || // Empty strings
                (typeof value === 'number' && isNaN(value)) // NaN
            ) {
                acc[1].push(item) // Add to empty elements
            } else {
                acc[0].push(item) // Add to valid elements
            }
            return acc
        },
        [[], []] as [T[], T[]]
    )

    // Sort valid elements
    const sortedValidElements = _.orderBy(
        validElements,
        [
            (item: T) => {
                const value = _.get(item, key)

                // Handle numeric fields directly
                if (typeof value === 'number') {
                    return value
                }

                // Handle date fields directly
                if (typeof value === 'object' && value instanceof Date) {
                    return value.getTime() // Sort by timestamp
                }

                if (typeof value === 'string') {
                    // Handle numeric strings by converting to numbers
                    if (!isNaN(Number(value))) {
                        return Number(value)
                    }

                    // Handle date strings
                    const dateValue = new Date(value)
                    if (!isNaN(dateValue.getTime())) {
                        return dateValue.getTime() // Sort by timestamp
                    }

                    // Handle simple names (alphabetical sorting)
                    return value.trim().toLowerCase()
                }

                // Move random gibberish to top or bottom
                return direction === 'asc'
                    ? Number.POSITIVE_INFINITY
                    : Number.NEGATIVE_INFINITY
            },
        ],
        [direction]
    )

    /**
     * Custom comparator function for sorting objects based on a specified key.
     *
     * @param a - The first object to compare.
     * @param b - The second object to compare.
     * @returns A number indicating the sort order. Returns a positive number if `a` should be sorted after `b`,
     *          a negative number if `a` should be sorted before `b`, or 0 if they are considered equal.
     *
     * The function uses `_.get` to safely access the values of the specified key from both objects.
     * If the key is 'poNumber', it performs a locale-aware string comparison with numeric sensitivity.
     * The sort direction is determined by the `direction` variable, which can be 'asc' for ascending
     * or 'desc' for descending order.
     */
    const customComparator = (a: T, b: T) => {
        const valueA = _.get(a, key)
        const valueB = _.get(b, key)

        switch (key) {
            case 'orderNumber':
            case 'poNumber':
            case 'building':
            case 'unit':
                // do localCompare for string values
                return direction === 'asc'
                    ? valueA.localeCompare(valueB, 'en', { numeric: true })
                    : valueB.localeCompare(valueA, 'en', { numeric: true })

            default:
                // return direction === 'asc' ? -1 : 1
                return 0
        }
    }

    // Concatenate empty elements at the end or beginning based on direction
    return direction === 'desc'
        ? [...sortedValidElements.sort(customComparator), ...emptyElements]
        : [...emptyElements, ...sortedValidElements.sort(customComparator)]
}

export const getPendingOrderDataTableRow = (
    order: GetPendingOrderAPIObject
) => {
    return {
        ...order,
        id: order.PODMO_TRANSACTION,
        poNumber: order.PODMO_CUST_PO,
        orderDate: convertToDate({
            value: order.PODMO_DATE,
            format: 'yyyyMMdd',
        }),
        installDate: convertToDate({
            value: order.PONOTE_DATE,
            format: 'yyyyMMdd',
        }),
        customerName: order.CADD_NAME,
        shipToAddress: order.PODMO_INSTALL_ADDR,
        billToAddress: order.C_ADDR1,
        unit: order.PODMO_UNIT,
        orderNumber: order.PODMO_TRANSACTION,
        addresses: `${order.PODMO_INSTALL_ADDR} ${order.PODMO_BUILDING} #${order.PODMO_UNIT}`,
        building: order.PODMO_BUILDING,
        orderedBy: order.PODMO_WEB_USER || order.DMH_ORDEREDBY,
        status: 'Pending',
        action: '',
    }
}

export const getInvoiceDataTableRow = (invoice: GetInvoiceSearchAPIObject) => {
    return {
        ...invoice,
        id: invoice.IVC_INVNO,
        invoiceNumber: invoice.IVC_INVNO,
        invoiceOrderNumber: invoice.IVC_ORDNO,
        poNumber: invoice.IVC_CUSTPO,
        invoiceDate: convertToDate({
            value: invoice.IVC_DATE,
            format: 'MMddyyyy',
        }),

        installDate: convertToDate({
            value: invoice.IVC_INSTALLDATE,
            format: 'MMddyyyy',
        }),
        dueDate: calculateDueDate(invoice),
        address1: invoice.DMH_SHADDR1.trim(),
        building: invoice?.BLDG_ONLY?.trim(),
        orderedBy: invoice.DMH_ORDEREDBY ? invoice.DMH_ORDEREDBY : '-',
        unit: invoice?.UNIT_ONLY,
        propType: invoice.PROPTYPE,

        amount: parseStringToDecimal(invoice.CURRENTBALANCE),

        balanceDue: parseStringToDecimal(invoice.R2_CBAL),
        // installDate: readableDate(order.PONOTE_DATE),
        // customerName: order.CADD_NAME,
        // addresses: `${order.CADD_ADDR1}
        // #${order.PODMO_UNIT}
        // ${order.PODMO_INSTALL_ADDR}`,
        // orderBy: order.PODMO_WEB_USER,
        // status: 'Pending',
        // action: '',
    }
}

export const calculateDueDate = (invoice: GetInvoiceSearchAPIObject) => {
    let termsDay = parseInt(invoice.R2_TNET)
    if (
        [NaN, 0].includes(parseInt(invoice.R2_TNET)) &&
        [NaN, 0].includes(parseInt(invoice.R2_TDAYS)) &&
        [NaN, 0].includes(parseInt(invoice.R2_TERMS))
    ) {
        termsDay = 30
    } else if (
        ![NaN, 0].includes(parseInt(invoice.R2_TDAYS)) &&
        ![NaN, 0].includes(parseInt(invoice.R2_TERMS))
    ) {
        termsDay = parseInt(invoice.R2_TDAYS)
    }
    return invoice.IVC_DATE === '00000000'
        ? ''
        : addDays(parse(invoice.IVC_DATE, 'MMddyyyy', new Date()), termsDay)
}

export const getInvoiceProrateDataTable = (
    invoiceProrate: InvoiceProrateObject
): InvoiceProrateType => {
    return {
        id: Number(invoiceProrate.PLN_LNSEQ),
        style: invoiceProrate.PLT_PD_ST,
        color: invoiceProrate.PLT_CO_TY,
        invoiceAmount: (parseFloat(invoiceProrate.PLT_EXT) / 100).toFixed(2),
        productLife: '-',
        monthUsed: '-',
        lostLife: '-',
        prorateAmount: '-',
        type: invoiceProrate.PLN_TYPE,
        buildingUnit: invoiceProrate.DMH_SHADDR2,
        property: invoiceProrate.DMH_SHNAME,
        propertyAddress: `${invoiceProrate.S_NAME}\n ${invoiceProrate.S_ADDR1}\n ${invoiceProrate.S_ADDR2 || ''}\n ${invoiceProrate.S_CTYST}\n ${invoiceProrate.S_ZIPCD}`,
    }
}

export const getOrderDataTableRow = (order: GetOrdersOrdFileAPIObject) => {
    return {
        ...order,
        status: isPast(
            new Date(
                convertToDate({
                    value: order.DMH_INSTALLDATE,
                    format: 'MMddyyyy',
                })
            )
        )
            ? 'Ordered'
            : 'Pending',
        id: order.DMO_ORDNO,
        orderDate: convertToDate({ value: order.DMH_DATE, format: 'MMddyyyy' }),
        customerName: order.CADD_NAME,
        addresses: `${order.DMH_SHADDR1} ${order.BLDG} #${order.UNIT}`,
        shipToAddress: order.DMH_SHADDR1,
        billToAddress: order.C_ADDR1,
        installDate: convertToDate({
            value: order.DMH_INSTALLDATE,
            format: 'MMddyyyy',
        }),
        building: order.BLDG,
        unit: order.UNIT,
        orderedBy: order.DMH_ORDEREDBY,
        action: '',
        orderNumber: order.DMO_ORDNO,
        poNumber: order.DMH_CUSTPO,
        invoiceDate: convertToDate({
            value: order.DMH_DATE,
            format: 'MMddyyyy',
        }),
        dueDate: convertToDate({
            value: order.DMH_INSTALLDATE,
            format: 'MMddyyyy',
        }),
    }
}

export const convertToDate = ({
    value,
    format = 'yyyyMMdd',
}: {
    value?: string
    format?: string
}) => {
    return !value || value === '00000000'
        ? ''
        : isValid(parse(value, format, new Date()))
          ? parse(value, format, new Date())
          : ''
}

export const getSortField = (sortField: string) => {
    switch (sortField) {
        case 'invoiceOrderNumber':
        case 'invoiceNumber':
        case 'poNumber':
            return 'invoiceNumber:invoiceOrderNumber:poNumber'
        case 'invoiceDate':
        case 'installDate':
        case 'dueDate':
            return 'invoiceDate:installDate:dueDate'
        case 'address1':
        case 'building':
        case 'unit':
            return 'address1:building:unit'
        case 'orderedBy':
            return 'orderedBy'
        case 'amount':
            return 'amount'
        case 'balanceDue':
            return 'balanceDue'
        default:
            return ''
    }
}

export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file) // Converts file to a Base64 string
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
    })
}

// Helper functions to calculate luminance and contrast ratio
export const getLuminance = (hexColor: string) => {
    const rgb = hexToRgb(hexColor)
    const [r, g, b] = rgb.map((value) => {
        const channel = value / 255
        return channel <= 0.03928
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const getContrastRatio = (bgColor: string, textColor: string) => {
    const bgLuminance = getLuminance(bgColor)
    const textLuminance = getLuminance(textColor)
    return (
        (Math.max(bgLuminance, textLuminance) + 0.05) /
        (Math.min(bgLuminance, textLuminance) + 0.05)
    )
}

export const hexToRgb = (hex: string) => {
    const normalizedHex = hex?.replace('#', '')
    const bigint = parseInt(normalizedHex, 16)
    return [bigint >> 16, (bigint >> 8) & 255, bigint & 255]
}

export const isReadable = (bgColor: string, textColor: string) => {
    const contrastRatio = getContrastRatio(bgColor, textColor)
    return contrastRatio >= 4.5
}

export const getReadableFontColor = (bgColor: string, textColor: string) => {
    return isReadable(bgColor, textColor)
        ? textColor
        : getContrastRatio(bgColor, '#000000') >
            getContrastRatio(bgColor, '#ffffff')
          ? '#000000'
          : '#ffffff'
}

export const handleGlobalError = ({
    error,
    userData,
}: {
    error: AxiosError
    userData: ReturnType<typeof useAuthHook>['data']
}) => {
    if (
        error?.status === 502 ||
        error?.status === 504 ||
        (error?.status === 403 &&
            error?.response?.data?.message.includes('Server is not reachable'))
    ) {
        throw new Error(
            'Something went wrong. Server is not reachable at the moment. Please try again later'
        )
    }
    Sentry.captureException('No locations Found', {
        extra: { error, userData },
    })

    throw new Error('No locations found')
}

export const convertBranches = (
    data: ReturnType<typeof useGetBranches>['data']
) => {
    return data?.map((item) => item.ID).join('/*\\')
}

export const convertToCgiString = (value: Array<string>) => {
    return value
        .reduce((acc, curr, index) => {
            if (value.length === 1) {
                return curr + '//**\\\\'
            }
            acc += curr + (index === value.length - 1 ? '' : '//**\\\\')
            return acc
        }, '')
        .replace(/Blank/g, '')
}
